import React, { useState } from 'react'
import { ShoppingCart, Package, Server, LogOut, Shield } from 'lucide-react'
import { BuyerOrderEntry } from './pages/BuyerOrderEntry'
import { BuyerInventory }  from './pages/BuyerInventory'
import { SellerDashboard } from './pages/SellerDashboard'
import { OperatorLanding } from './pages/OperatorLanding'
import { Login }           from './pages/Login'
import { useAuth }         from './auth/AuthContext'
import { BuyerProvisioning } from './pages/BuyerProvisioning'

// ─── Mode ─────────────────────────────────────────────────────────────────────
// 'unified' → auth-gated portal with role-based routing.
// 'buyer'   → legacy single-role build (login disabled).
// 'seller'  → legacy single-role build (login disabled).
const MODE: 'buyer' | 'seller' | 'unified' =
  (typeof __VITE_MODE__ !== 'undefined' ? __VITE_MODE__ : 'unified') as any

const HOST_PORT: string =
  typeof __VITE_HOST_PORT__ !== 'undefined' ? __VITE_HOST_PORT__ : '4000'

// ─── Tabs per role ────────────────────────────────────────────────────────────
type Tab = 'order' | 'inventory' | 'provisioning' | 'seller' | 'operator'
type Role = 'buyer' | 'seller' | 'operator'

const ROLE_TABS: Record<Role, { id: Tab; label: string; icon: React.ReactNode; sidebar?: boolean }[]> = {
  buyer: [
    { id: 'order',     label: 'New RFQ',       icon: <ShoppingCart size={15} />, sidebar: true  },
    { id: 'inventory', label: 'Dashboard', icon: <Package      size={15} />, sidebar: true  },
    { id: 'provisioning', label: 'Provisioning', icon: <Shield      size={15} />, sidebar: true  },
  ],
  seller: [
    { id: 'seller',    label: 'Dashboard',        icon: <Server       size={15} />, sidebar: true },
  ],
  operator: [
    { id: 'operator',  label: 'Console',      icon: <Shield       size={15} /> },
  ],
}



// ─── Component ────────────────────────────────────────────────────────────────
export default function App() {
  // In legacy single-role builds (buyer / seller) skip auth entirely.
  if (MODE !== 'unified') return <LegacyApp />

  return <UnifiedApp />
}

// ─── Legacy single-role (kept for backward compat with dev:buyer / dev:seller) ─
function LegacyApp() {
  const role: Role = MODE === 'seller' ? 'seller' : 'buyer'
  const tabs = ROLE_TABS[role]
  const [tab, setTab] = useState<Tab>(tabs[0].id)
  return (
    <Shell
      role={role}
      tabs={tabs}
      activeTab={tab}
      onTabChange={setTab}
      backendLabel={`Flask :${HOST_PORT}`}
      onLogout={null}
    >
      <TabContent tab={tab} onNavigate={setTab} />
    </Shell>
  )
}

// ─── Unified (auth-gated) ─────────────────────────────────────────────────────
function UnifiedApp() {
  const { user, loading, logout } = useAuth()
  const [tab, setTab] = useState<Tab>('order')

  // Snap the active tab to one that exists for the current role.
  // Done in an effect so it runs after the role becomes known and on role change.
  React.useEffect(() => {
    if (!user) return
    const allowed = ROLE_TABS[user.role as Role]
    if (!allowed.find(t => t.id === tab)) setTab(allowed[0].id)
  }, [user?.role])

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#0D1B35', color: '#94A3B8', fontSize: '0.85rem',
      }}>
        Loading…
      </div>
    )
  }

  if (!user) return <Login />

  const role = user.role as Role
  const tabs = ROLE_TABS[role]
  const activeTab = tabs.find(t => t.id === tab) ? tab : tabs[0].id

  return (
    <Shell
      role={role}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setTab}
      backendLabel={user.username ?? user.email}
      onLogout={logout}
    >
      <TabContent tab={activeTab} onNavigate={setTab} />
    </Shell>
  )
}

// ─── Content router ───────────────────────────────────────────────────────────
function TabContent({ tab, onNavigate }: { tab: Tab; onNavigate: (t: Tab) => void }) {
  switch (tab) {
    case 'order':     return <BuyerOrderEntry onSubmitSuccess={() => onNavigate('inventory')} />
    case 'inventory': return <BuyerInventory  />
    case 'provisioning': return <BuyerProvisioning />
    case 'seller':    return <SellerDashboard />
    case 'operator':  return <OperatorLanding />
  }
}


// ─── Shell (header + footer + main) ───────────────────────────────────────────
function Shell({role, tabs, activeTab, onTabChange, backendLabel, onLogout, children,
}: {
  role: Role
  tabs: { id: Tab; label: string; icon: React.ReactNode;sidebar?: boolean }[]
  activeTab: Tab
  onTabChange: (t: Tab) => void
  backendLabel: string
  onLogout: (() => Promise<void> | void) | null
  children: React.ReactNode
}) {
  const modeColor =
    role === 'seller'   ? '#7C3AED' :   // purple
    role === 'operator' ? '#D97706' :   // amber
                          '#0D8F7A'     // teal (buyer)

  const roleTag =
    role === 'seller'   ? 'SELLER'   :
    role === 'operator' ? 'OPERATOR' :
                          'BUYER'

  const sidebarTabs = tabs.filter(t => t.sidebar)
  const headerTabs  = tabs.filter(t => !t.sidebar)
  const hasSidebar  = sidebarTabs.length > 0

  return (
    <div style={{ minHeight:'100vh', background:'#F1F5F9', display:'flex', flexDirection:'column' }}>
      <header style={{
        background: '#0D1B35',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0.75rem 1.5rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
          <svg
      viewBox="0 0 220 80"
      height="36"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block', flexShrink: 0 }}
    >
      {/* Red lightning bolt shape */}
      <polygon
        points="138,2 175,2 155,38 178,38 130,78 150,42 128,42"
        fill="#D94F4F"
      />
      {/* FIRE in white bold */}
      <text
        x="2" y="62"
        fontFamily="Arial Black, Arial, sans-serif"
        fontWeight="900"
        fontSize="62"
        fill="white"
        letterSpacing="-1"
      >FIRE</text>
      {/* x in white bold, overlapping bolt */}
      <text
        x="133" y="62"
        fontFamily="Arial Black, Arial, sans-serif"
        fontWeight="900"
        fontSize="62"
        fill="white"
      >x</text>
    </svg>

          <span style={{
            padding: '0.15rem 0.6rem', borderRadius: '9999px',
            fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.05em',
            background: `${modeColor}40`, color: '#E2E8F0',
            border: `1px solid ${modeColor}80`,
          }}>
            {roleTag}
          </span>

          <span style={{ color: '#475569', fontSize: '0.7rem' }}>
            Welcome, {backendLabel}
          </span>

          <span style={{ color: '#ff0000', fontSize: '0.7rem' }}>
            <strong>Pilot version</strong>. Report bugs or feedback to FIREx team.
          </span>
        </div>

        <div style={{ display:'flex', gap:'0.25rem', alignItems: 'center' }}>
          {headerTabs.map(t => (
            <button
              key={t.id}
              onClick={() => onTabChange(t.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.375rem',
                padding: '0.375rem 0.75rem', borderRadius: '0.25rem',
                fontSize: '0.75rem', fontWeight: 600,
                border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                background: activeTab === t.id ? modeColor : 'transparent',
                color:      activeTab === t.id ? '#fff'    : '#94A3B8',
              }}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
          {onLogout && (
            <button
              onClick={() => onLogout()}
              title="Sign out"
              style={{
                marginLeft: '0.5rem',
                display: 'flex', alignItems: 'center', gap: '0.375rem',
                padding: '0.375rem 0.65rem', borderRadius: '0.25rem',
                fontSize: '0.75rem', fontWeight: 600,
                backgroundColor: '#7C3AED',
                color: '#fff',
                border: '1px solid #334155', cursor: 'pointer',
              }}
            >
              <LogOut size={13} />
              Sign out
            </button>
          )}
        </div>
      </header>

      {/* ── Body ── */}
      <div style={{ flex:1, display:'flex', overflow:'hidden' }}>

        {/* ── Sidebar — only for buyer ── */}
        {hasSidebar && (
          <aside style={{
            width: '200px', flexShrink: 0,
            background: '#0D1B35',
            display: 'flex', flexDirection: 'column',
            padding: '1.25rem 0',
            boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
          }}>
            <nav style={{ display:'flex', flexDirection:'column', gap:'0.25rem', padding:'0 0.75rem' }}>
              {sidebarTabs.map(t => (
                <button key={t.id} onClick={() => onTabChange(t.id)} style={{
                  display: 'flex', alignItems: 'center', gap: '0.625rem',
                  padding: '0.625rem 0.875rem', borderRadius: '0.375rem',
                  fontSize: '0.82rem', fontWeight: 600,
                  border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                  textAlign: 'left', width: '100%',
                  background: activeTab === t.id ? `${modeColor}25` : 'transparent',
                  color:      activeTab === t.id ? '#fff' : '#94A3B8',
                  borderLeft: activeTab === t.id ? `3px solid ${modeColor}` : '3px solid transparent',
                }}>
                  {t.icon}{t.label}
                </button>
              ))}
            </nav>
            <div style={{ flex:1 }} />
            <div style={{ padding:'0 1rem 0.75rem' }}>
              <span style={{
                display:'block', textAlign:'center',
                padding:'0.25rem 0.5rem', borderRadius:'9999px',
                fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.05em',
                background:`${modeColor}30`, color: modeColor,
                border:`1px solid ${modeColor}60`,
              }}>{roleTag}</span>
            </div>
          </aside>
        )}

      <main style={{ flex: 1, padding: '1.5rem', overflow: 'auto' }}>
        {children}
      </main>
      </div>

      <footer style={{
        padding: '0.5rem 1.5rem',
        fontSize: '0.7rem', color: '#94A3B8',
        borderTop: '1px solid #E2E8F0', background: '#fff',
        display: 'flex', justifyContent: 'space-between',
      }}>
        <span>CRPO-GC5-ETH-002 · Singapore-ETH Centre · ETH Zurich · Chain 13371</span>
      </footer>
    </div>
  )
}
